import { useCallback, useEffect, useRef } from "react";

import { useGscConnection } from "./useGscConnection";

/**
 * Wraps `useGscConnection` with an authentication gate.
 *
 * Call `trigger()` to perform an action that requires a GSC connection:
 * - If already connected, `onAuthenticated` fires immediately.
 * - If not connected, the OAuth popup opens first; `onAuthenticated`
 *   fires automatically once the connection succeeds.
 *
 * @param onAuthenticated - Stable callback (wrap with `useCallback`).
 *   Called synchronously when already connected, or after OAuth success.
 */
export const useGscAuthGate = (onAuthenticated: () => void) => {
    const gscConnection = useGscConnection();
    const { currentConnection, connect, isConnecting } = gscConnection;
    const pendingActionRef = useRef(false);

    useEffect(() => {
        if (pendingActionRef.current && currentConnection && !isConnecting) {
            pendingActionRef.current = false;
            onAuthenticated();
        }
    }, [currentConnection, isConnecting, onAuthenticated]);

    const trigger = useCallback(() => {
        if (currentConnection) {
            onAuthenticated();
        } else {
            pendingActionRef.current = true;
            connect();
        }
    }, [currentConnection, connect, onAuthenticated]);

    return { ...gscConnection, trigger };
};
